<template>
  <div>
    <!-- Informações do Workflow -->
    <b-row class="match-height">
      <b-col cols="12">
        <b-card>
          <template #header>
            <div class="d-flex align-items-center justify-content-between">
              <h4 class="mb-0">
                <feather-icon icon="SettingsIcon" size="21" class="mr-50"/>
                Configuração do Workflow
              </h4>
            </div>
          </template>

          <b-card-body>
            <b-row>
              <b-col md="6">
                <div class="mb-2">
                  <h6 class="text-muted mb-50">Nome do Workflow</h6>
                  <p class="font-weight-bold">{{ model.name || '-' }}</p>
                </div>
              </b-col>
              <b-col md="6">
                <div class="mb-2">
                  <h6 class="text-muted mb-50">Data de Criação</h6>
                  <p class="font-weight-bold">{{ formatDate(model.created_at) }}</p>
                </div>
              </b-col>
              <b-col cols="12">
                <div>
                  <h6 class="text-muted mb-50">Descrição</h6>
                  <p v-html-safe="model.description || '-'"></p>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <div class="d-flex justify-content-end gap-2">
                  <b-button
                    v-permission="'newsletter_workflows.edit'"
                    variant="outline-primary"
                    size="sm"
                    @click="goToEdit(0)">
                    <feather-icon icon="EditIcon" size="14" />
                    Editar
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="goBack">
                    <feather-icon icon="ArrowLeftIcon" class="mr-1"/>
                    Voltar
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Cronogramas (Timelines) -->
    <b-row class="match-height mt-2">
      <b-col cols="12">
        <b-card>
          <template #header>
            <div class="d-flex align-items-center justify-content-between">
              <h4 class="mb-0">
                <feather-icon icon="CalendarIcon" size="21" class="mr-50"/>
                {{ $t('newsletter.workflow.timeline') }}
              </h4>

            </div>
          </template>

          <b-card-body>
            <div v-if="timelines.length === 0" class="text-center py-3">
              <feather-icon icon="CalendarIcon" size="48" class="text-muted mb-1"/>
              <p class="text-muted">Nenhum cronograma configurado</p>
            </div>

            <div v-else class="timeline-accordion">
              <b-card
                v-for="(timeline, index) in timelines"
                :key="timeline.id"
                no-body
                class="mb-2">

                <!-- Cabeçalho do Accordion -->
                <b-card-header
                  header-tag="header"
                  class="p-1"
                  role="tab">
                  <div
                    class="flex items-center w-full p-1 cursor-pointer transition-colors duration-200"
                    style="border-radius: 6px;"
                    v-b-toggle="`accordion-${timeline.id}`"
                    @mouseenter="$event.currentTarget.style.backgroundColor = '#e5e7eb'"
                    @mouseleave="$event.currentTarget.style.backgroundColor = 'transparent'">
                    <div class="flex-shrink-0 mr-4">
                      <b-avatar
                        :variant="getTimelineVariant(timeline.status)"
                        size="45">
                        <span class="font-weight-bold">{{ index + 1 }}</span>
                      </b-avatar>
                    </div>
                    <div class="flex-1 min-w-0">
                      <h5 class="font-weight-bold">
                        {{ timeline.newsletter.title }}
                        <b-badge
                          :variant="getTimelineBadgeVariant(timeline.status)"
                          class="ml-1">
                          {{ timeline.status || 'PREVISTO' }}
                        </b-badge>
                      </h5>
                      <div class="mb-1">
                        <small class="text-muted">
                          <span v-if="timeline.newsletter.group_news_name || timeline.newsletter.subgroup_news_name">
                            {{ timeline.newsletter.group_news_name || '-' }} | {{ timeline.newsletter.subgroup_news_name || '-' }}
                          </span>
                          <span v-else>Sem grupo definido</span>
                        </small>
                      </div>
                      <div class="flex items-center flex-wrap gap-4">
                        <div class="flex items-center">
                          <feather-icon icon="CalendarIcon" size="16" class="mr-2 text-primary"/>
                          <span class="text-primary font-weight-bold">
                            {{ formatDate(timeline.start_at) }} - {{ formatDate(timeline.end_at) }}
                          </span>
                        </div>
                        <div v-if="timeline.checkpoints && timeline.checkpoints.length > 0" class="flex items-center">
                          <feather-icon icon="CheckCircleIcon" size="14" class="mr-2 text-muted"/>
                          <small class="text-muted">{{ timeline.checkpoints.length }} ponto(s) de verificação</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-card-header>

                <!-- Conteúdo do Accordion - Checkpoints -->
                <b-collapse
                  :id="`accordion-${timeline.id}`"
                  accordion="timeline-accordion"
                  role="tabpanel">
                  <b-card-body>
                    <div v-if="!timeline.checkpoints || timeline.checkpoints.length === 0" class="text-center">
                      <feather-icon icon="InfoIcon" size="32" class="text-muted mb-1"/>
                      <p class="text-muted mb-0">
                        {{ $t('newsletter.workflow.checkpoint.no_checkpoints_configured') }}
                      </p>
                    </div>

                    <div v-else>
                      <div
                        v-for="(checkpoint, cpIndex) in timeline.checkpoints"
                        :key="checkpoint.id || cpIndex"
                        class="checkpoint-section mb-3">

                        <!-- Cabeçalho do Checkpoint -->
                        <div class="d-flex align-items-center justify-content-between mb-2">
                          <div class="d-flex align-items-center">
                            <b-avatar
                              :variant="getCheckpointVariant(checkpoint.status)"
                              size="32"
                              class="mr-2 checkpoint-avatar">
                              <feather-icon icon="CheckCircleIcon" size="16"/>
                            </b-avatar>
                            <div>
                              <h6 class="mb-0">
                                Ponto de Verificação - {{ formatDate(checkpoint.checkpoint_at) }} -
                                <b-badge
                                  :variant="getCheckpointBadgeVariant(checkpoint.status)"
                                  class="checkpoint-badge">
                                  {{ checkpoint.status || 'PREVISTO' }}
                                </b-badge>
                              </h6>
                            </div>
                          </div>

                          <!-- Ações do Checkpoint -->
                          <div class="checkpoint-actions">
                            <vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer">
                              <feather-icon
                                icon="MoreVerticalIcon"
                                class="h-5 w-5 stroke-current text-muted">
                              </feather-icon>
                              <vs-dropdown-menu>
                                <vs-dropdown-item v-permission="'newsletter_workflows.detail'">
                                  <div
                                    class="flex checkpoint-menu-item"
                                    @click="generateCheckpointReport(timeline, checkpoint)">
                                    <div class="w-2/12">
                                      <feather-icon
                                        icon="FileTextIcon"
                                        svgClasses="h-5 w-5 mb-1 stroke-current text-primary"/>
                                    </div>
                                    <div class="w-10/12">
                                      <div class="flex text-primary font-medium">
                                        {{ $t('action.generate_report') }}
                                      </div>
                                    </div>
                                  </div>
                                </vs-dropdown-item>
                              </vs-dropdown-menu>
                            </vs-dropdown>
                          </div>
                        </div>

                        <!-- Estatísticas do Checkpoint -->
                        <b-row v-if="checkpoint.data" class="mt-2">
                          <b-col lg="4" md="6" sm="12" class="mb-1">
                            <statistic-card-horizontal
                              icon="EyeIcon"
                              :statistic="checkpoint.data.views_count || 0"
                              statistic-title="Visualizações"
                              color="primary"
                            />
                          </b-col>
                          <b-col lg="4" md="6" sm="12" class="mb-1">
                            <statistic-card-horizontal
                              icon="HeartIcon"
                              :statistic="checkpoint.data.likes_count || 0"
                              statistic-title="Curtidas"
                              color="danger"
                            />
                          </b-col>
                          <b-col lg="4" md="6" sm="12" class="mb-1">
                            <statistic-card-horizontal
                              icon="MessageSquareIcon"
                              :statistic="checkpoint.data.comments_count || 0"
                              statistic-title="Comentários"
                              color="warning"
                            />
                          </b-col>
                          <b-col lg="4" md="6" sm="12" class="mb-1">
                            <statistic-card-horizontal
                              icon="EditIcon"
                              :statistic="checkpoint.data.signatures_count || 0"
                              statistic-title="Assinaturas"
                              color="success"
                            />
                          </b-col>
                          <b-col lg="4" md="6" sm="12" class="mb-1">
                            <statistic-card-horizontal
                              icon="AlertCircleIcon"
                              :statistic="checkpoint.data.comments_not_accepted_count || 0"
                              statistic-title="Comentários Rejeitados"
                              color="danger"
                            />
                          </b-col>
                          <b-col lg="4" md="6" sm="12" class="mb-1">
                            <statistic-card-horizontal
                              icon="ClockIcon"
                              :statistic="checkpoint.data.comments_without_moderator_count || 0"
                              statistic-title="Aguardando Moderação"
                              color="info"
                            />
                          </b-col>
                        </b-row>

                        <!-- Mensagem se não houver estatísticas -->
                        <b-alert v-else show variant="light" class="mb-0 text-center">
                          <div class="d-flex align-items-center justify-content-center">
                            <feather-icon icon="InfoIcon" size="18" class="mr-50"/>
                            <span>Estatísticas não disponíveis para este ponto de verificação</span>
                          </div>
                        </b-alert>

                        <!-- Listagem de Relatórios Gerados -->
                        <div v-if="checkpoint.media && checkpoint.media.length > 0" class="mt-3">
                          <h6 class="mb-2">
                            <feather-icon icon="FileTextIcon" size="16" class="mr-50"/>
                            Relatórios Gerados ({{ checkpoint.media.length }})
                          </h6>
                          <div class="reports-list">
                            <div
                              v-for="media in getSortedMedia(checkpoint)"
                              :key="media.id"
                              class="report-item">
                              <div class="report-info">
                                <feather-icon icon="FileIcon" size="18" class="mr-2 text-danger"/>
                                <div class="flex-grow-1">
                                  <p class="mb-0 font-weight-bold">{{ media.name }}</p>
                                  <small class="text-muted">
                                    Gerado em {{ formatDateTime(media.created_at) }} • {{ formatFileSize(media.size) }}
                                  </small>
                                </div>
                              </div>
                              <div class="report-actions">
                                <b-button
                                  variant="outline-info"
                                  size="sm"
                                  @click="previewReport(media)"
                                  v-b-tooltip.hover
                                  title="Visualizar Relatório">
                                  <feather-icon icon="EyeIcon" size="14"/>
                                </b-button>
                                <b-button
                                  variant="outline-primary"
                                  size="sm"
                                  @click="downloadReport(media)"
                                  v-b-tooltip.hover
                                  title="Baixar Relatório">
                                  <feather-icon icon="DownloadIcon" size="14"/>
                                </b-button>
                                <b-button
                                  variant="outline-warning"
                                  size="sm"
                                  @click="sendSpecificCheckpointReport(timeline, checkpoint, media)"
                                  v-b-tooltip.hover
                                  title="Enviar Relatório">
                                  <feather-icon icon="SendIcon" size="14"/>
                                </b-button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <hr v-if="cpIndex < timeline.checkpoints.length - 1" class="my-3"/>
                      </div>
                    </div>
                  </b-card-body>
                </b-collapse>
              </b-card>
            </div>

            <div class="d-flex justify-content-end gap-2 pt-2">
              <b-button
                v-permission="'newsletter_workflows.edit'"
                variant="outline-primary"
                size="sm"
                @click="goToEdit(1)">
              <feather-icon icon="EditIcon" size="14" class="mr-50"/>
              {{ $t('action.manage') }}
            </b-button>
          </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Destinatários -->
    <b-row class="match-height mt-2">
      <b-col cols="12">
        <b-card>
          <template #header>
            <div class="d-flex align-items-center justify-content-between">
              <h4 class="mb-0">
                <feather-icon icon="UsersIcon" size="21" class="mr-50"/>
                Destinatários
              </h4>
            </div>
          </template>

          <b-card-body>
            <grid
              ref="grid-destination"
              v-if="service && id"
              :hide_actions="true"
              :service="service"
              :route_grid_path="`/${id}/destinations/grid`"
              route_name="newsletter_workflows"
              :column_formats="{
                created_at: (val) => {
                  return $utils.format.date(val)
                },
                dispatched_at: (val) => {
                  return $utils.format.date(val)
                },
                status : (val) =>{
                  return ''+val.label
                }
              }"
              :css_formats="{
                status: (val) => {
                 return 'text-'+val.color
                }
              }"
            >
            </grid>

            <div class="d-flex justify-content-end gap-2">
            <b-button
                v-permission="'newsletter_workflows.edit'"
                variant="outline-primary"
                size="sm"
                @click="goToEdit(2)">
                <feather-icon icon="EditIcon" size="14" class="mr-50"/>
                {{ $t('action.manage') }}
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Modal de Preview de Arquivo -->
    <file-preview-modal
      :show="showPreviewModal"
      :file="selectedFile"
      :preview-url="selectedFile.url_preview"
      @close="closePreview"
      @download="downloadSelectedFile"
    />
  </div>
</template>

<script>

import standard from '@/services/standard'
import { isAdmin } from '@/util/Util'
import {
  BForm, BButton, BFormGroup, BInputGroup, BFormTextarea, BCardText, BFormInput,
  BCard, BCardBody, BRow, BCol, BAvatar, VBTooltip, BCardHeader, BCollapse, BAlert,
  VBToggle, BBadge
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import StatisticCardHorizontal from '@/@core/components/statistics-cards/StatisticCardHorizontal.vue'
import FilePreviewModal from '@/components/FileExplorer/FilePreviewModal.vue'

import NewsletterWorkflowService from '@/services/api/NewsletterWorkflowService'

export default {

  directives: {
    'b-tooltip': VBTooltip,
    'b-toggle': VBToggle,
  },
  components: {
    BForm,
    BButton,
    BFormGroup,
    BInputGroup,
    BFormTextarea,
    BCardText,
    BFormInput,
    BCard,
    BCardBody,
    BRow,
    BCol,
    BAvatar,
    BCardHeader,
    BCollapse,
    BAlert,
    BBadge,
    // validations
    ValidationProvider,
    ValidationObserver,
    StatisticCardHorizontal,
    FilePreviewModal
  },
  props: {
    id: {
      default: null,
      type: Number
    }
  },

  data: () => ({
    service: null,
    model: {
      id: null,
      name : '',
      description: '',
      created_at: null
    },
    timelines: [],
    showPreviewModal: false,
    selectedFile: {},
  }),
  computed: {

  },
  beforeMount() {
    this.service = NewsletterWorkflowService.build(this.$vs)
  },
  mounted() {
    if (this.id) {
      this.loadData(this.id)
      this.loadTimelines(this.id)
    }
  },

  methods: {
    getSortedMedia(checkpoint) {
      if (!checkpoint.media || checkpoint.media.length === 0) {
        return []
      }
      return [...checkpoint.media].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at)
      })
    },
    loadData(id) {
      this.$vs.loading()

      this.service.read(id).then(
        response => {
          this.model = response
        },
        error => {
          this.$vs.loading.close()
        }
      ).finally(() => {this.$vs.loading.close()})
    },
    loadTimelines(id) {
      this.service.getAllTimelines(id).then(
        response => {
          this.timelines = response || []
        },
        error => {
          console.error('Erro ao carregar timelines:', error)
        }
      )
    },
    formatDate(date) {
      if (!date) return '-'
      return this.$moment(date).format('DD/MM/YYYY')
    },
    formatDateTime(date) {
      if (!date) return '-'
      return this.$moment(date).format('DD/MM/YYYY HH:mm')
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    },
    downloadReport(media) {
      if (media.url_tmp) {
        // Abre a URL em uma nova aba para forçar o download
        window.open(media.url_tmp, '_blank')
        this.notifySuccess(this.$vs, 'Download iniciado!', 2000)
      } else {
        this.notifyError(this.$vs, 'URL do relatório não disponível', 3000)
      }
    },
    previewReport(media) {
      if (media.url_tmp) {
        this.selectedFile = {
          name: media.file_name || media.name,
          mime_type: media.mime_type || 'application/pdf',
          extension: media.file_name ? media.file_name.split('.').pop() : 'pdf',
          url_preview: media.url_preview,
          url_tmp: media.url_tmp
        }
        this.showPreviewModal = true
      } else {
        this.notifyError(this.$vs, 'URL do relatório não disponível para preview', 3000)
      }
    },
    closePreview() {
      this.showPreviewModal = false
      this.selectedFile = {}
    },
    downloadSelectedFile() {
      if (this.selectedFile.url_tmp) {
        window.open(this.selectedFile.url_tmp, '_blank')
        this.notifySuccess(this.$vs, 'Download iniciado!', 2000)
      }
    },
    sendSpecificCheckpointReport(timeline, checkpoint, media) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: this.$t('action.send_report'),
        text: `Deseja enviar o relatório "${media.name}" para todos os destinatários cadastrados?`,
        acceptText: 'Sim, enviar',
        cancelText: 'Cancelar',
        accept: () => {
          this.$vs.loading()

          this.service.sendCheckpointReport(this.id, timeline.id, checkpoint.id, media.id).then(
            response => {
              this.notifySuccess(this.$vs, 'Relatório enviado com sucesso!', 3000)
            }
          ).catch(
            error => {
              this.notifyError(this.$vs, 'Erro ao enviar relatório.', 5000)
            }
          ).finally(() => {
            this.$vs.loading.close()
          })
        }
      })
    },
    getCheckpointVariant(status) {
      // Retorna a variante do avatar baseado no status
      switch (status) {
        case 'REALIZADO':
          return 'light-success'
        case 'EM ANDAMENTO':
          return 'light-warning'
        case 'PREVISTO':
          return 'light-secondary'
        default:
          return 'light-secondary'
      }
    },
    getCheckpointBadgeVariant(status) {
      // Retorna a variante do badge baseado no status
      switch (status) {
        case 'REALIZADO':
          return 'success'
        case 'EM ANDAMENTO':
          return 'warning'
        case 'PREVISTO':
          return 'secondary'
        default:
          return 'secondary'
      }
    },
    getTimelineVariant(status) {
      // Retorna a variante do avatar da timeline baseado no status
      switch (status) {
        case 'REALIZADO':
          return 'light-success'
        case 'EM ANDAMENTO':
          return 'light-warning'
        case 'PREVISTO':
          return 'light-secondary'
        default:
          return 'light-secondary'
      }
    },
    getTimelineBadgeVariant(status) {
      // Retorna a variante do badge da timeline baseado no status
      switch (status) {
        case 'REALIZADO':
          return 'success'
        case 'EM ANDAMENTO':
          return 'warning'
        case 'PREVISTO':
          return 'secondary'
        default:
          return 'secondary'
      }
    },
    generateCheckpointReport(timeline, checkpoint) {
      this.$vs.loading()

      this.service.generateReportCheckpoint(this.id, timeline.id, checkpoint.id).then(
        response => {
          this.notifySuccess(this.$vs, 'Relatório será gerado em breve, aguarde a notificação de conclusão ou o recarregamento da página automaticamente!', 5000)
          this.notifySuccess(this.$vs, 'Relatório solicitado com sucesso!', 3000)
          setTimeout(() => {
            this.loadData(this.id)
          }, 5000)
        }
      ).catch(
        error => {
          this.notifyError(this.$vs, 'Erro ao gerar relatório do checkpoint.', 5000)
        }
      ).finally(() => {
        this.$vs.loading.close()
      })
    },
    goToEdit(tabIndex = 0) {
      this.$router.push({
        name: 'newsletter-workflow-edit',
        params: { id: this.id },
        query: { tab: tabIndex }
      })
    },
    goBack() {
      this.$router.push('/newsletter_workflows')
    },
    isAdmin() {
      return isAdmin()
    },
    isEdit() {
      return !_.isNil(this.id)
    },
  }
}
</script>

<style scoped>
.timeline-accordion {
  width: 100%;
}

/* Garante que o avatar fique sempre redondo */
::v-deep .b-avatar {
  flex-shrink: 0;
}

.checkpoint-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

/* Opacidade nos status dos checkpoints para diferenciar das timelines */
::v-deep .checkpoint-avatar {
  opacity: 0.7;
}

::v-deep .checkpoint-badge {
  opacity: 0.75;
}

.checkpoint-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.checkpoint-menu-item {
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
  width: 200px;
}

.checkpoint-menu-item:hover {
  background-color: #f8f9fa;
}

/* Listagem de Relatórios */
.reports-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.report-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background-color: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  transition: all 0.2s;
}

.report-item:hover {
  border-color: var(--primary);
  box-shadow: 0 2px 8px rgba(var(--vs-primary), 0.15);
}

.report-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.report-info .flex-grow-1 {
  flex-grow: 1;
}

.report-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.report-actions .btn {
  padding: 0.25rem 0.5rem;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 1rem;
}

.match-height {
  display: flex;
  flex-wrap: wrap;
}

.match-height > [class*='col'] {
  display: flex;
  flex-direction: column;
}

/* Estilos para o accordion */
::v-deep .card-header {
  background-color: #fff;
  border-bottom: 1px solid #ebe9f1;
}

::v-deep .collapsed > .when-opened,
::v-deep:not(.collapsed) > .when-closed {
  display: none;
}
</style>
